import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface TaskWatchButtonProps {
  taskId: string;
  unaId: string;
  currentUserId: string;
}

export const TaskWatchButton = ({ taskId, unaId, currentUserId }: TaskWatchButtonProps) => {
  const [isWatching, setIsWatching] = useState(false);
  const [watchSettings, setWatchSettings] = useState({
    watch_type: 'all' as 'all' | 'comments' | 'status_changes' | 'assignments',
    email_notifications: true,
    in_app_notifications: true,
  });

  useEffect(() => {
    fetchWatchStatus();
  }, [taskId]);

  const fetchWatchStatus = async () => {
    const { data } = await supabase
      .from('task_watchers')
      .select('*')
      .eq('task_id', taskId)
      .eq('user_id', currentUserId)
      .maybeSingle();

    if (data) {
      setIsWatching(true);
      setWatchSettings({
        watch_type: (data.watch_type as 'all' | 'comments' | 'status_changes' | 'assignments') || 'all',
        email_notifications: data.email_notifications ?? true,
        in_app_notifications: data.in_app_notifications ?? true,
      });
    }
  };

  const handleToggleWatch = async () => {
    try {
      if (isWatching) {
        await supabase
          .from('task_watchers')
          .delete()
          .eq('task_id', taskId)
          .eq('user_id', currentUserId);

        setIsWatching(false);
        toast.success('Stopped watching task');
      } else {
        await supabase
          .from('task_watchers')
          .insert({
            task_id: taskId,
            user_id: currentUserId,
            una_id: unaId,
            ...watchSettings
          });

        setIsWatching(true);
        toast.success('Now watching task');
      }
    } catch (error) {
      toast.error('Failed to update watch status');
    }
  };

  const updateWatchSettings = async (updates: Partial<typeof watchSettings>) => {
    const newSettings = { ...watchSettings, ...updates };
    setWatchSettings(newSettings);

    if (isWatching) {
      try {
        await supabase
          .from('task_watchers')
          .update(updates)
          .eq('task_id', taskId)
          .eq('user_id', currentUserId);

        toast.success('Watch settings updated');
      } catch (error) {
        toast.error('Failed to update settings');
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={isWatching ? 'default' : 'outline'}
          size="sm"
          className={cn(
            "gap-2",
            isWatching && "bg-primary text-primary-foreground"
          )}
        >
          {isWatching ? <Bell className="h-4 w-4 fill-current" /> : <BellOff className="h-4 w-4" />}
          {isWatching ? 'Watching' : 'Watch'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Watch Settings</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleToggleWatch}>
          {isWatching ? (
            <>
              <BellOff className="h-4 w-4 mr-2" />
              Stop watching
            </>
          ) : (
            <>
              <Bell className="h-4 w-4 mr-2" />
              Start watching
            </>
          )}
        </DropdownMenuItem>

        {isWatching && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Notification Types
            </DropdownMenuLabel>

            <DropdownMenuCheckboxItem
              checked={watchSettings.email_notifications}
              onCheckedChange={(checked) => updateWatchSettings({ email_notifications: checked })}
            >
              Email notifications
            </DropdownMenuCheckboxItem>

            <DropdownMenuCheckboxItem
              checked={watchSettings.in_app_notifications}
              onCheckedChange={(checked) => updateWatchSettings({ in_app_notifications: checked })}
            >
              In-app notifications
            </DropdownMenuCheckboxItem>

            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Notify me on
            </DropdownMenuLabel>

            <DropdownMenuItem onClick={() => updateWatchSettings({ watch_type: 'all' })}>
              <div className={cn(
                "h-2 w-2 rounded-full mr-2",
                watchSettings.watch_type === 'all' ? "bg-primary" : "bg-muted"
              )} />
              All activity
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => updateWatchSettings({ watch_type: 'comments' })}>
              <div className={cn(
                "h-2 w-2 rounded-full mr-2",
                watchSettings.watch_type === 'comments' ? "bg-primary" : "bg-muted"
              )} />
              Comments only
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => updateWatchSettings({ watch_type: 'status_changes' })}>
              <div className={cn(
                "h-2 w-2 rounded-full mr-2",
                watchSettings.watch_type === 'status_changes' ? "bg-primary" : "bg-muted"
              )} />
              Status changes
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => updateWatchSettings({ watch_type: 'assignments' })}>
              <div className={cn(
                "h-2 w-2 rounded-full mr-2",
                watchSettings.watch_type === 'assignments' ? "bg-primary" : "bg-muted"
              )} />
              Assignments
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
